import { z } from 'zod';
import { UsdAmount } from './ai.js';
/**
 * Khu vực vẽ và sơ đồ trong `.chilldoc` — APM-DRW-b, docs/plan-drawing.md §3, §5.3.
 *
 * Hai thứ tách bạch:
 *
 * - `DrawingAttrs` — thuộc tính của node `drawing` trong tệp: kích thước khu
 *   vực + element của Excalidraw (kiểm **lỏng**: schema của Excalidraw đổi theo
 *   bản, kiểm chặt là tệp cũ mở không được).
 * - `DiagramSpec` — cái AI sinh ra: nút, cạnh,
 *   kiểu sơ đồ. **Không có toạ độ** — dàn trang là việc của hàm tất định ở web,
 *   để sơ đồ vừa khu vực ở đúng cỡ chữ người dùng chọn.
 */
// ---------------------------------------------------------------------------
// Style
// ---------------------------------------------------------------------------
export const DRAW_STYLES = ['sketch', 'sales', 'scientific', 'minimal'];
export const DrawStyle = z.enum(DRAW_STYLES);
/** Token của từng style — web vẽ và AI viết nhãn đọc cùng một chỗ. */
export const DRAW_STYLE_TOKENS = {
    sketch: {
        roughness: 2,
        strokeWidth: 2,
        fillStyle: 'hachure',
        rounded: true,
        fontFamily: 'Excalifont',
        stroke: '#1e1e1e',
        text: '#1e1e1e',
        fills: ['#ffec99', '#b2f2bb', '#a5d8ff', '#ffc9c9', '#d0bfff'],
        strongFill: '#ffd43b',
        strongText: '#1e1e1e',
        mutedFill: '#e9ecef',
        edge: '#1e1e1e',
        tone: 'thân mật, ngắn gọn như ghi chú tay trên bảng trắng',
    },
    sales: {
        roughness: 0,
        strokeWidth: 1,
        fillStyle: 'solid',
        rounded: true,
        fontFamily: 'Nunito',
        stroke: '#1864ab',
        text: '#0b2545',
        fills: ['#d0ebff', '#d3f9d8', '#fff3bf', '#ffe3e3'],
        strongFill: '#1971c2',
        strongText: '#ffffff',
        mutedFill: '#f1f3f5',
        edge: '#1864ab',
        tone: 'thuyết phục, bắt đầu bằng động từ mạnh, nhấn vào lợi ích cho khách hàng',
    },
    scientific: {
        roughness: 0,
        strokeWidth: 1,
        fillStyle: 'solid',
        rounded: false,
        fontFamily: 'Liberation Sans',
        stroke: '#212529',
        text: '#212529',
        fills: ['#ffffff', '#f1f3f5'],
        strongFill: '#dee2e6',
        strongText: '#212529',
        mutedFill: '#ffffff',
        edge: '#495057',
        tone: 'chính xác, dùng thuật ngữ chuẩn, không cảm thán, không phóng đại',
    },
    minimal: {
        roughness: 0,
        strokeWidth: 1,
        fillStyle: 'solid',
        rounded: true,
        fontFamily: 'Nunito',
        stroke: '#495057',
        text: '#212529',
        fills: ['transparent'],
        strongFill: 'transparent',
        strongText: '#1971c2',
        mutedFill: 'transparent',
        edge: '#868e96',
        tone: 'tối giản, mỗi nhãn vài chữ',
    },
};
// ---------------------------------------------------------------------------
// Bộ màu — đè màu lên style, giữ nguyên nét / độ nhám / font / bo góc
// ---------------------------------------------------------------------------
export const DRAW_PALETTES = ['ocean', 'forest', 'sunset', 'pastel', 'colorblind'];
export const DrawPalette = z.enum(DRAW_PALETTES);
/**
 * Năm bộ màu — mỗi bộ một tông rõ ràng, màu tô nhạt để chữ tối luôn đọc được,
 * màu nhấn đủ đậm cho chữ trắng. `colorblind` là bảng Okabe–Ito: phân biệt
 * được với cả người mù màu đỏ–lục, và khi in đen trắng.
 */
export const DRAW_PALETTE_COLORS = {
    ocean: {
        fills: ['#dbeafe', '#ccfbf1', '#e0f2fe', '#e0e7ff'],
        accents: ['#1d4ed8', '#0f766e', '#0369a1', '#4338ca'],
        hachureFills: ['#93c5fd', '#5eead4', '#7dd3fc', '#a5b4fc'],
        highlight: '#3b82f6',
        muted: '#f1f5f9',
        stroke: '#1e3a5f',
        text: '#0f172a',
        edge: '#334e68',
    },
    forest: {
        fills: ['#dcfce7', '#ecfccb', '#d1fae5', '#fef9c3'],
        accents: ['#15803d', '#4d7c0f', '#047857', '#a16207'],
        hachureFills: ['#86efac', '#bef264', '#6ee7b7', '#fde047'],
        highlight: '#22c55e',
        muted: '#f4f4f0',
        stroke: '#14532d',
        text: '#1a2e05',
        edge: '#3f6212',
    },
    sunset: {
        fills: ['#ffedd5', '#fee2e2', '#fef3c7', '#fce7f3'],
        accents: ['#c2410c', '#b91c1c', '#b45309', '#be185d'],
        hachureFills: ['#fdba74', '#fca5a5', '#fcd34d', '#f9a8d4'],
        highlight: '#f97316',
        muted: '#faf5f0',
        stroke: '#7c2d12',
        text: '#431407',
        edge: '#9a3412',
    },
    pastel: {
        fills: ['#ede9fe', '#fce7f3', '#dbeafe', '#dcfce7', '#fef3c7'],
        accents: ['#7e22ce', '#be185d', '#1d4ed8', '#15803d', '#a16207'],
        hachureFills: ['#c4b5fd', '#f9a8d4', '#93c5fd', '#86efac', '#fcd34d'],
        highlight: '#a855f7',
        muted: '#f5f5f4',
        stroke: '#52525b',
        text: '#27272a',
        edge: '#71717a',
    },
    colorblind: {
        fills: ['#cce3f0', '#f7d9c4', '#c6eadf', '#f2dcea', '#fbe7bf', '#d9eefb'],
        accents: ['#0072b2', '#b54d00', '#00785a', '#a3487d', '#8a5c00', '#1f6f9e'],
        hachureFills: ['#7fb8dc', '#f0a878', '#66c2a5', '#e0a3c8', '#f5c860', '#9fd3f2'],
        highlight: '#e69f00',
        muted: '#f2f2f2',
        stroke: '#222222',
        text: '#111111',
        edge: '#444444',
    },
};
/** Token của style sau khi áp bộ màu; `null` ⇒ màu gốc của style. */
export function drawTokens(style, palette) {
    const base = DRAW_STYLE_TOKENS[style];
    if (!palette)
        return { ...base, strokes: [base.stroke] };
    const colors = DRAW_PALETTE_COLORS[palette];
    // Tối giản không tô: giữ bản chất ấy, bộ màu đi vào viền từng nút và chữ nhấn.
    if (base.fills.every((fill) => fill === 'transparent')) {
        return {
            ...base,
            text: colors.text,
            edge: colors.edge,
            stroke: colors.stroke,
            strongText: colors.accents[0],
            strokes: colors.accents,
        };
    }
    const solid = base.fillStyle === 'solid';
    return {
        ...base,
        stroke: colors.stroke,
        text: colors.text,
        edge: colors.edge,
        fills: solid ? colors.fills : colors.hachureFills,
        strongFill: solid ? colors.accents[0] : colors.highlight,
        strongText: solid ? '#ffffff' : colors.text,
        mutedFill: colors.muted,
        strokes: [colors.stroke],
    };
}
// ---------------------------------------------------------------------------
// Khu vực vẽ
// ---------------------------------------------------------------------------
export const DRAWING_WIDTH = { min: 240, max: 1600 };
export const DRAWING_HEIGHT = { min: 160, max: 2000 };
/** Cỡ chữ tối thiểu người dùng chọn khi sinh sơ đồ (px) — dưới mức này là khó đọc. */
export const DRAWING_MIN_FONT = { min: 10, max: 32, default: 14 };
/** Trần một hình trong tệp: `JSON.stringify({ elements, spec })`. */
export const MAX_DRAWING_BYTES = 300_000;
/** Id của frame = khu vực vẽ trong scene Excalidraw. */
export const DRAWING_FRAME_ID = 'chilldoc-drawing-frame';
/**
 * Mã Mermaid một khối code được phép mang — và cũng là trần của mã gốc giữ lại
 * trong hình vẽ chuyển từ Mermaid (APM-MMD-a). Mermaid tự đặt trần 50 000 ký tự;
 * sơ đồ đọc được bằng mắt chưa bao giờ tới 20 000.
 */
export const MERMAID_SOURCE_MAX = 20_000;
export const DRAWING_SIZE_PRESETS = [
    { id: 'small', width: 480, height: 270 },
    { id: 'medium', width: 640, height: 360 },
    { id: 'large', width: 800, height: 450 },
    { id: 'portrait', width: 480, height: 640 },
];
export const DrawingBox = z.object({
    width: z.number().int().min(DRAWING_WIDTH.min).max(DRAWING_WIDTH.max),
    height: z.number().int().min(DRAWING_HEIGHT.min).max(DRAWING_HEIGHT.max),
});
/** Kẹp kích thước khu vực vào trần — ô "Tuỳ chỉnh" và "Nới khu vực" đều đi qua đây. */
export function clampDrawingBox(width, height) {
    const clamp = (value, range) => Math.min(range.max, Math.max(range.min, Math.round(Number.isFinite(value) ? value : range.min)));
    return { width: clamp(width, DRAWING_WIDTH), height: clamp(height, DRAWING_HEIGHT) };
}
// ---------------------------------------------------------------------------
// DiagramSpec
// ---------------------------------------------------------------------------
export const DIAGRAM_TYPES = [
    'flow',
    'tree',
    'mindmap',
    'cycle',
    'timeline',
    'comparison',
];
export const DiagramType = z.enum(DIAGRAM_TYPES);
export const DIAGRAM_SHAPES = [
    'rectangle',
    'rounded',
    'ellipse',
    'diamond',
    'parallelogram',
    'hexagon',
    'cylinder',
    'document',
    'cloud',
    'star',
    'callout',
];
export const DiagramShape = z.enum(DIAGRAM_SHAPES);
export const DIAGRAM_MAX_NODES = 40;
export const DIAGRAM_MAX_EDGES = 60;
export const DIAGRAM_MAX_GROUPS = 8;
export const DIAGRAM_LABEL_MAX = 120;
export const DIAGRAM_TITLE_MAX = 80;
/** Đoạn chữ gửi cho AI khi sinh sơ đồ. */
export const DIAGRAM_TEXT_MAX = 4000;
// `.trim()` **trước** `.min()`: chuỗi toàn dấu cách không được lọt qua (bẫy CLAUDE.md).
const Id = z.string().trim().min(1).max(40);
const Label = z.string().trim().min(1).max(DIAGRAM_LABEL_MAX);
export const DiagramNode = z.object({
    id: Id,
    label: Label,
    shape: DiagramShape,
    emphasis: z.enum(['normal', 'strong', 'muted']),
    /** Id nhóm (cột của `comparison`, làn của `flow`); `null` = không nhóm. */
    group: Id.nullable(),
});
export const DiagramEdge = z.object({
    from: Id,
    to: Id,
    label: z.string().trim().max(60).nullable(),
});
export const DiagramGroup = z.object({ id: Id, label: z.string().trim().min(1).max(60) });
/**
 * Mọi trường **bắt buộc** (có thể `null`): đây cũng là schema nộp kết quả của
 * model — trường tuỳ chọn là chỗ model hay bỏ sót. Ràng buộc chéo (id trùng,
 * cạnh trỏ nút không có) là việc của `finalize` ở skill, để model được sửa.
 */
export const DiagramSpec = z.object({
    type: DiagramType,
    title: z.string().trim().max(DIAGRAM_TITLE_MAX).nullable(),
    nodes: z.array(DiagramNode).min(1).max(DIAGRAM_MAX_NODES),
    edges: z.array(DiagramEdge).max(DIAGRAM_MAX_EDGES),
    groups: z.array(DiagramGroup).max(DIAGRAM_MAX_GROUPS),
});
/** Lỗi chéo của một spec — rỗng là hợp lệ. Skill dùng để từ chối, web dùng để chặn spec hỏng. */
export function diagramSpecProblems(spec) {
    const problems = [];
    const ids = new Set();
    for (const node of spec.nodes) {
        if (ids.has(node.id))
            problems.push(`Id nút "${node.id}" bị trùng.`);
        ids.add(node.id);
    }
    const groups = new Set(spec.groups.map((group) => group.id));
    for (const node of spec.nodes) {
        if (node.group !== null && !groups.has(node.group)) {
            problems.push(`Nút "${node.id}" thuộc nhóm "${node.group}" không có trong groups.`);
        }
    }
    spec.edges.forEach((edge, index) => {
        if (!ids.has(edge.from) || !ids.has(edge.to)) {
            problems.push(`Cạnh #${index} (${edge.from} → ${edge.to}) trỏ tới nút không có.`);
        }
    });
    return problems;
}
// ---------------------------------------------------------------------------
// Yêu cầu sinh sơ đồ (params của skill `generate_diagram`)
// ---------------------------------------------------------------------------
export const DiagramCapacity = z.object({
    maxNodes: z.number().int().min(1).max(DIAGRAM_MAX_NODES),
    maxLabelChars: z.number().int().min(8).max(DIAGRAM_LABEL_MAX),
});
export const DiagramRequest = z.object({
    /** Đoạn chữ người dùng bôi. */
    text: z.string().trim().min(1).max(DIAGRAM_TEXT_MAX),
    type: z.enum(['auto', ...DIAGRAM_TYPES]),
    style: DrawStyle,
    box: DrawingBox,
    minFont: z.number().int().min(DRAWING_MIN_FONT.min).max(DRAWING_MIN_FONT.max),
    /** Ước lượng của web: bao nhiêu nút, nhãn dài bao nhiêu thì vừa khu vực ở `minFont`. */
    capacity: DiagramCapacity,
    /** Lượt "Rút gọn nhãn": giữ nguyên id của spec trước, nhãn ≤ `maxLabelChars`. */
    shorten: z
        .object({
        maxLabelChars: z.number().int().min(8).max(DIAGRAM_LABEL_MAX),
        spec: DiagramSpec,
    })
        .nullable(),
});
/** `POST /me/files/diagram-plan` — APM-DRW-g. `prompt`: yêu cầu thêm của người dùng. */
export const DiagramGenerateInput = DiagramRequest.extend({
    prompt: z.string().trim().max(500).optional(),
    runId: z.uuid().optional(),
});
export const DiagramGenerateResult = z.object({
    spec: DiagramSpec,
    /** Chuỗi thập phân như mọi số tiền AI khác — không làm tròn qua `number`. */
    costUsd: UsdAmount,
    partial: z.boolean(),
});
// ---------------------------------------------------------------------------
// Node `drawing` trong tệp
// ---------------------------------------------------------------------------
/** Element Excalidraw — chỉ đòi `id`, `type`; phần còn lại để nguyên cho Excalidraw đọc. */
export const DrawingElement = z.looseObject({ id: z.string(), type: z.string() });
export const DrawingAttrs = z.object({
    width: z.number().int().min(DRAWING_WIDTH.min).max(DRAWING_WIDTH.max),
    height: z.number().int().min(DRAWING_HEIGHT.min).max(DRAWING_HEIGHT.max),
    elements: z.array(DrawingElement).max(5000),
    style: DrawStyle.nullable().catch(null),
    /** Bộ màu đè lên style; vắng / `null` ⇒ màu gốc của style. */
    palette: DrawPalette.nullish().catch(null),
    spec: DiagramSpec.nullable().catch(null),
    /** Người dùng đã sửa tay sau khi sinh từ `spec` — dàn lại sẽ mất chỉnh tay. */
    specStale: z.boolean().catch(false),
    /** Cỡ chữ tối thiểu lúc sinh từ `spec` — đổi khu vực/style thì dàn lại không nhỏ hơn mức này. */
    minFont: z
        .number()
        .int()
        .min(DRAWING_MIN_FONT.min)
        .max(DRAWING_MIN_FONT.max)
        .catch(DRAWING_MIN_FONT.default)
        .default(DRAWING_MIN_FONT.default),
    /**
     * Mã Mermaid của hình chuyển từ khối code (APM-MMD-a) — để "Trở lại mã Mermaid"
     * và để LLM đọc được nguồn. Hình vẽ tay/AI: không có.
     */
    mermaid: z.string().max(MERMAID_SOURCE_MAX).nullish().catch(null),
});
/** Số byte một hình chiếm trong tệp — so với `MAX_DRAWING_BYTES`. */
export function drawingBytes(attrs) {
    return new TextEncoder().encode(JSON.stringify({ elements: attrs.elements, spec: attrs.spec, mermaid: attrs.mermaid ?? null })).length;
}
//# sourceMappingURL=drawing.js.map